import { getLink } from "@utils/linkUtils";
import { AnimatePresence, motion } from "framer-motion";
import { useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import Image from "next/image";
import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import FocusLock from "react-focus-lock";

import ContactChannels from "@/components/ContactChannels/ContactChannels";
import AnchorLink from "@/components/ui/AnchorLink/AnchorLink";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import { NavigationContext } from "@/context/NavigationContext";
import { TemplateContext } from "@/context/TemplateContext";
import { UserContext } from "@/context/UserContext";
import { ctaClickTracking } from "@/dataLayer/dataLayer";
import useBreakPoint from "@/hooks/useBreakpoint";
import useScroll from "@/hooks/useScroll";
import { HeaderProps } from "@/types";

import NavigationPanelCorp from "./NavigationPanelCorp";

const SoftLogin = dynamic(() => import("@/components/SoftLogin/SoftLogin"), { ssr: false });

const whiteMenu = {
  darkLogo: "block",
  lightLogo: "hidden",
  iconStroke: "stroke-black",
  hamburgerVariant: "dark",
  textColor: "text-black",
  bgColor: "bg-white shadow-strong",
};

const transparentMenu = {
  darkLogo: "hidden",
  lightLogo: "block",
  iconStroke: "stroke-white",
  hamburgerVariant: "light",
  textColor: "text-white",
  bgColor: "bg-transparent shadow-none",
};

const HeaderDesktop: FC<HeaderProps> = ({ logoDark, logoLight, menu, personalArea, shortlist, contactChannels, corporate, settings }) => {
  const scroll = useScroll();
  const t = useTranslations();
  const locale = useLocale();
  const desktopHeaderRef = useRef<HTMLDivElement>(null);
  const [trigger, setTrigger] = useState<HTMLElement>();
  const { fixedHeader } = useContext(TemplateContext);
  const { data: session } = useSession();
  const { isMd } = useBreakPoint("md");

  const {
    desktopPanelID,
    setDesktopPanelID,
    setSettingsVisible,
    setLanguagesVisible,
    languagesVisible,
    settingsVisible,
    transitionComplete,
    setDesktopHeaderHeight,
  } = useContext(NavigationContext);

  const { language } = useContext(UserContext);

  const scrollClasses: Record<string, string> = useMemo(() => {
    // quando è sticky dev'essre sempre white
    if (!fixedHeader) {
      return whiteMenu;
    } else {
      // in questo caso è fixed, trasparente con scroll a 0
      if (scroll === 0) {
        if (desktopPanelID !== "") {
          return whiteMenu;
        } else {
          return whiteMenu;
        }
      } else {
        return whiteMenu;
      }
    }
  }, [scroll, desktopPanelID, fixedHeader]);

  const keyboardHandler = useCallback(
    (event: KeyboardEvent) => {
      if (desktopPanelID !== "" && event.key.toLowerCase() === "escape") {
        setDesktopPanelID("");
      }
    },
    [desktopPanelID, setDesktopPanelID],
  );
  useEffect(() => {
    if (document) {
      document.addEventListener("keydown", keyboardHandler);
      return () => {
        document.removeEventListener("keydown", keyboardHandler);
      };
    }
  }, [keyboardHandler]);

  const languageCallback = useCallback(() => {
    if (settingsVisible) {
      setLanguagesVisible(languagesVisible);
    }
    if (desktopPanelID) {
      setDesktopPanelID("");
    }
    setLanguagesVisible(!languagesVisible);
  }, [settingsVisible, languagesVisible, desktopPanelID, setDesktopPanelID, setLanguagesVisible]);

  const settingsCallback = useCallback(() => {
    if (languagesVisible) {
      setLanguagesVisible(settingsVisible);
    }
    if (desktopPanelID) {
      setDesktopPanelID("");
    }
    setSettingsVisible(!settingsVisible);
  }, [settingsVisible, languagesVisible, desktopPanelID, setDesktopPanelID, setLanguagesVisible, setSettingsVisible]);

  useEffect(() => {
    if (desktopHeaderRef.current && isMd) {
      setDesktopHeaderHeight(desktopHeaderRef.current.clientHeight);
    }
  }, [desktopHeaderRef, setDesktopHeaderHeight, isMd]);

  const transition = {
    duration: 0.3,
  };

  return (
    <div
      ref={desktopHeaderRef}
      id="header-desktop"
      className={`${scrollClasses.bgColor} ${fixedHeader ? "fixed" : "sticky"} ${settings?.settings?.preheader ? "h-[88px]" : "h-[60px]"} transition-all w-full top-0 hidden md:block z-50`}
    >
      {/* Back header */}
      {settings?.settings?.preheader && (
        <div className="bg-black relative h-[28px] flex items-center">
          <div className="flex justify-between container py-xxs">
            <div className="flex items-center gap-s">
              <AnimatePresence>
                {language && (
                  <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={languageCallback} disabled={!transitionComplete}>
                    <div className="flex items-center text-white">
                      <span className="font-secondary text-sm">
                        {t("header.label.languages")}: {language.toUpperCase()}
                      </span>
                      <Icon size="16" className="stroke-white" name="caret-down" />
                    </div>
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
            <ContactChannels
              desktop={true}
              contactChannels={contactChannels}
              customClass="text-white font-secondary text-s"
              variation="stroke-white"
            />
          </div>
        </div>
      )}
      {/* Back header */}
      {/* Main header */}
      <div className="backdrop-blur-2xl h-[60px]">
        <div className={`container flex h-full justify-between items-center px-5xl`}>
          <div className="flex items-center gap-l">
            <AnchorLink href={`/${locale}/`} aria-label={t("button.label.homepage")}>
              <Image
                className={`m-auto max-w-[133px] md:max-w-[170px] md:m-0 ${scrollClasses.darkLogo}`}
                src={logoDark?.url}
                width={114}
                height={30}
                alt={logoDark?.title}
              />
              <Image
                className={`m-auto max-w-[133px] md:max-w-[170px] md:m-0 ${scrollClasses.lightLogo}`}
                src={logoLight?.url}
                width={114}
                height={30}
                alt={logoDark?.title}
              />
            </AnchorLink>
          </div>
          <nav className="flex gap-l">
            {menu?.items?.map((item, index) => {
              const itemHref = item.menuItemCta ? getLink(item.menuItemCta, locale).toString() : "#";
              return (
                <div key={item.id} className="relative">
                  <Button
                    className={`${desktopPanelID === item.id && "underline"} hover:underline underline-offset-4 font-secondary text-[14px] flex justify-between items-center ${scrollClasses.textColor}`}
                    variant={ButtonVariant.TERTIARY}
                    size={ButtonSize.SMALL}
                    theme={ButtonTheme.BLACK}
                    href={itemHref}
                    type="button"
                    onClick={() => {
                      if ((!item.columns || item.columns.length === 0) && item.menuItemCta) {
                        // window.location.href = getLink(item.menuItemCta, locale).toString();
                      } else {
                        if (desktopPanelID === item.id) {
                          setDesktopPanelID("");
                        } else {
                          setDesktopPanelID(item.id);
                        }
                      }
                      ctaClickTracking(item.title || "", "HeaderDesktopCorporate", "HeaderDesktopCorporate");
                    }}
                  >
                    <span>{item.title}</span>
                  </Button>
                  <NavigationPanelCorp
                    item={item}
                    active={desktopPanelID === item.id}
                    transition={transition}
                    setDesktopPanelID={setDesktopPanelID}
                    trigger={trigger}
                    setTrigger={setTrigger}
                  />
                </div>
              );
            })}
          </nav>
        </div>
      </div>
    </div>
  );
};
export default HeaderDesktop;
